/* ============================================================
   ratings.js — the one shared source for star ratings, used by every
   product card, the PDP, and the cart drawer/page.

   Ported from the static build's js/ratings.js — same handle-keyed
   override map, same deterministic hash fallback, same public API
   (window.GhoncheRatings). Only the data source URL changed: it now
   fetches the theme asset copy of data/ratings.json (assets/ratings.json)
   instead of /data/ratings.json. This is the exact seam SHOPIFY-PORT.md
   flagged: swap this one fetch for a real reviews app's product
   metafields (Judge.me/Okendo) later, and nothing in the files that
   call GhoncheRatings needs to change.

   New in the port: applyStarsFromProof() fills every card's
   .card__proof[data-rating-proof] element (an empty hook left by
   snippets/product-card.liquid) — the static build's category.js/
   search.js built this markup client-side from catalog.json; here the
   card HTML already exists (rendered by Liquid), so this just needs to
   fill in the rating text once preload() resolves.
   ============================================================ */
(function () {
  "use strict";

  var URL = window.GHONCHE_RATINGS_URL || "/data/ratings.json";
  var overrides = null;
  var pending = null;

  function hashFallback(handle) {
    var h = 0;
    for (var i = 0; i < handle.length; i++) h = (h * 31 + handle.charCodeAt(i)) >>> 0;
    return { rating: Number((4.6 + (h % 31) / 100).toFixed(1)), reviews: 40 + (h % 260) };
  }

  function preload() {
    if (pending) return pending;
    pending = fetch(URL, { cache: "no-store" })
      .then(function (res) { return res.ok ? res.json() : {}; })
      .catch(function () { return {}; })
      .then(function (data) { overrides = data || {}; applyStarsFromProof(); return overrides; });
    return pending;
  }

  function get(handle) {
    /* Priority: Theme Editor block override (window.GhoncheReviewOverrides,
       set per-product via a product picker on the header section - see
       sections/header.liquid) > the static ratings.json file (legacy) >
       the deterministic hash fallback so every product still shows
       something plausible with zero setup. */
    var customizerOverride = window.GhoncheReviewOverrides && window.GhoncheReviewOverrides[handle];
    var o = customizerOverride || (overrides && overrides[handle]);
    var fallback = hashFallback(handle);
    return {
      rating: o && o.rating != null ? Number(o.rating) : fallback.rating,
      reviews: o && o.reviews != null ? Number(o.reviews) : fallback.reviews
    };
  }

  function fillPercent(rating) {
    return Math.max(0, Math.min(100, (Number(rating) || 0) / 5 * 100));
  }

  var STARS_INNER = '★★★★★<span class="stars__fill" aria-hidden="true">★★★★★</span>';

  function starsHTML(rating) {
    return '<span class="stars" style="--fill:' + fillPercent(rating) + '%">' + STARS_INNER + '</span>';
  }

  function applyStars(el, rating) {
    if (!el) return;
    el.style.setProperty("--fill", fillPercent(rating) + "%");
    el.innerHTML = STARS_INNER;
  }

  /* Handle for a [data-rating-proof] element: prefer an explicit
     data-product-handle on the element itself or a close ancestor (the
     main PDP's own rating badge has no nearby product link to read from
     - it's already on that product's page - so it carries this
     attribute directly, see sections/main-product.liquid). Otherwise
     look for a product link in the same card/article and read the
     handle from its real href (/products/{handle}), same technique
     js/main.js's wishlist sync uses. */
  function handleFromProofEl(el) {
    var withHandle = el.closest("[data-product-handle]");
    if (withHandle) return withHandle.getAttribute("data-product-handle") || "";
    var scope = el.closest("article, .gcart-line, .pdp-paired-mini") || el.parentElement;
    var link = scope && (scope.querySelector('a[href*="/products/"]'));
    if (!link) return "";
    var m = (link.getAttribute("href") || "").match(/\/products\/([^/?#]+)/);
    return m ? m[1] : "";
  }

  function applyStarsFromProof(root) {
    (root || document).querySelectorAll("[data-rating-proof]").forEach(function (el) {
      var handle = handleFromProofEl(el);
      if (!handle) return;
      var r = get(handle);
      if (el.classList.contains("card__proof") || el.classList.contains("pdp-info__rating")) {
        var starsEl = el.querySelector(".stars, .pdp-info__stars");
        if (starsEl) {
          applyStars(starsEl, r.rating);
        } else {
          el.insertAdjacentHTML("afterbegin", starsHTML(r.rating));
        }
        var textEl = el.querySelector("[data-pdp-rating-text]");
        if (textEl) {
          textEl.textContent = r.rating + " (" + r.reviews + " reviews)";
        } else if (!el.classList.contains("pdp-info__rating")) {
          el.insertAdjacentText("beforeend", " " + r.rating + " (" + r.reviews + ")");
        }
      } else {
        el.innerHTML = starsHTML(r.rating);
      }
    });
  }

  window.GhoncheRatings = {
    preload: preload, get: get, starsHTML: starsHTML, applyStars: applyStars,
    applyStarsFromProof: applyStarsFromProof
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () { preload(); });
  } else {
    preload();
  }

  /* Re-apply after Shopify section re-renders in the theme editor (new
     [data-rating-proof] hooks can appear without a full page reload). */
  document.addEventListener("shopify:section:load", function (e) { applyStarsFromProof(e.target); });
})();
